import { useState } from 'react';
import { AlertCircle, Mail, Lock, Eye, EyeOff, LogIn } from 'lucide-react';
import Reveal from '../components/animations/Reveal';
import AuthBackground from '../components/animations/AuthBackground';
import { loginUser, saveAuthSession } from '../api/authApi';

const INPUT_CLASS =
  'input-anim w-full bg-white/95 border border-white/40 rounded-xl p-3 pl-11 pr-11 text-slate-800 placeholder-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/30 focus:outline-none transition-all';

const LoginPage = ({ setActiveTab, redirectTab }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const { token, user } = await loginUser({ email: email.trim(), password });
      saveAuthSession({ token, user });
      setActiveTab?.(redirectTab || 'analysis');
    } catch (err) {
      setError(err.message || 'Login failed. Check your email and password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative flex flex-col items-center pt-16 pb-16 px-4">
      <AuthBackground />
      <div className="relative max-w-md w-full mx-auto sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center mb-6">
            <h2 className="font-display text-4xl font-bold text-white mb-3 drop-shadow-sm">Login</h2>
            <p className="text-xl text-white/80">Access your CryMiner account</p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="relative">
                <Mail className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Enter your email"
                />
              </div>

              <div className="relative">
                <Lock className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                </button>
              </div>

              {error && (
                <div className="flex items-start space-x-3 bg-red-500/10 border border-red-400/30 rounded-2xl p-4">
                  <AlertCircle className="w-5 h-5 text-red-300 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-200">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="brand-btn w-full px-6 py-3 rounded-xl font-semibold hover:scale-[1.02] transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  'Logging in...'
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Login
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setActiveTab?.('forgot-password')}
                  className="text-white/70 hover:text-white text-sm transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default LoginPage;